import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import {
  AppButton,
  AppTable,
  AppSpin,
  AppPagination,
  AppTooltip,
  AppTableActionButton,
} from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';

import type { ColumnsType } from 'antd/es/table';

import type { ConsentListItem } from '@/services/main/consent';
import { useConsentList, ConsentListFilters } from '@/hooks/main/useConsentList';
import { useConsentDelete } from '@/hooks/main/useDeleteConsentMutation';
import { useConsentRouteParams } from '@/hooks/main/useConsentRouteParams';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { getAntdTableSortOrder } from '@/utils';
import {
  CONSENT_DELETE_ERROR_FALLBACK,
  consentDeleteSuccessToastMessage,
  WALKING_CONDITION_ID,
} from '@/constants/consentForm';
import useAppToast from '@/hooks/useAppToast';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import useAppModal from '@/hooks/useAppModal';
import { getApiErrorMessage } from '@/utils/apiError';
import { convGtJDate } from '@/utils/dateConverter';
import { NotFound } from '@/pages/main/system/NotFound';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';
import { DEFAULT_PER_PAGE } from '@/constants';

export const ConsentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { serviceType: contextServiceType } = useTheme();
  const { tenant } = useParams();
  const toast = useAppToast();
  const { confirm } = useAppModal();

  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  const derived = useConsentRouteParams();

  const filters = useMemo<ConsentListFilters>(
    () => ({
      page: currentPage,
      per_page: DEFAULT_PER_PAGE,
      sort_type: sortField,
      sort_dir: sortOrder,
    }),
    [currentPage, sortField, sortOrder]
  );

  const {
    data: listResponse,
    isLoading,
    isFetching,
    isError,
  } = useConsentList(derived.patientId, filters);
  const deleteMutation = useConsentDelete(derived.patientId);
  const isBusy = isLoading || isFetching || deleteMutation.isPending;

  const listData = listResponse?.data;
  const patient = listData?.patient;
  const consents = listData?.consents ?? [];
  const totalCount = listData?.total_count ?? consents.length;
  const pagination = listData?.pagination;

  const isR2 = (contextServiceType?.toUpperCase() ?? '') === SERVICE_TYPE_R2;

  useEffect(() => {
    if (!isLoading && !isError && consents.length === 0 && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [isLoading, isError, consents.length, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSortChange = (_pagination: any, _filters: any, sorter: any) => {
    if (isFetching) return;
    const sort = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!sort?.columnKey || !sort?.order) {
      setSortField(undefined);
      setSortOrder(undefined);
    } else {
      setSortField(String(sort.columnKey));
      setSortOrder(sort.order === 'descend' ? 'desc' : 'asc');
    }

    setCurrentPage(1);
  };

  const handleDeleteConfirm = useCallback(
    async (record: ConsentListItem) => {
      try {
        const response = await deleteMutation.mutateAsync(record.id);
        toast.success(consentDeleteSuccessToastMessage(response?.message));
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, CONSENT_DELETE_ERROR_FALLBACK));
      }
    },
    [deleteMutation, toast]
  );

  const columns = useMemo<ColumnsType<ConsentListItem>>(() => {
    const formatJpDate = (val: string | null | undefined) => (val ? convGtJDate(val) : '');
    const formatJpPeriod = (from: string | null | undefined, to: string | null | undefined) => {
      const fromStr = formatJpDate(from);
      const toStr = formatJpDate(to);
      if (!fromStr && !toStr) return '';
      if (!fromStr) return <span className="whitespace-nowrap">{toStr}</span>;
      if (!toStr) return <span className="whitespace-nowrap">{fromStr}～</span>;
      return (
        <>
          <span className="whitespace-nowrap">{fromStr}～</span>
          <span className="whitespace-nowrap">{toStr}</span>
        </>
      );
    };

    const baseColumns: ColumnsType<ConsentListItem> = [
      {
        title: '同意書連番',
        dataIndex: 'serial_number',
        key: 'serial_number',
        align: 'center',
        width: 60,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'serial_number', sortOrder as any),
      },
      {
        title: '同意日',
        dataIndex: 'consent_date',
        key: 'consent_date',
        width: 180,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'consent_date', sortOrder as any),
        render: (value: string | null, record) => {
          const dateStr = formatJpDate(value);
          return (
            <span>
              {dateStr}
              {record.is_special ? <strong className="ml-1 font-bold">特</strong> : null}
            </span>
          );
        },
      },
      {
        title: '要加療期間',
        key: 'treatment_term',
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'treatment_term', sortOrder as any),
        render: (_, record) => {
          return formatJpPeriod(record.treatment_term_from, record.treatment_term_to);
        },
      },
      {
        title: '医療機関名',
        dataIndex: 'hospital_name',
        key: 'hospital_name',
        width: 200,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'hospital_name', sortOrder as any),
        render: (_: unknown, record: ConsentListItem) => (
          <AppTooltip title={record.hospital_name || ''} showOnlyOnOverflow>
            <div className="break-all line-clamp-2">{record.hospital_name || ''}</div>
          </AppTooltip>
        ),
      },
      {
        title: '医師名',
        dataIndex: 'doctor_name',
        key: 'doctor_name',
        width: 140,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'doctor_name', sortOrder as any),
        render: (_: unknown, record: ConsentListItem) => (
          <AppTooltip title={record.doctor_name || ''} showOnlyOnOverflow>
            <div className="break-all line-clamp-2">{record.doctor_name || ''}</div>
          </AppTooltip>
        ),
      },
    ];

    /** 往療 — 必要とする→「必要」、必要としない→「不要」（A2 consents / R2 r_consents 共通） */
    const walkingConditionColumn: ColumnsType<ConsentListItem>[number] = {
      title: '往療',
      dataIndex: 'walking_condition_id',
      key: 'walking_condition_id',
      align: 'center',
      width: 70,
      sorter: true,
      sortOrder: getAntdTableSortOrder(sortField || '', 'walking_condition_id', sortOrder as any),
      render: (value: number | null | undefined) => {
        if (value === WALKING_CONDITION_ID.REQUIRED) return '必要';
        if (value === WALKING_CONDITION_ID.NOT_REQUIRED) return '不要';
        return '';
      },
    };

    const r2Columns: ColumnsType<ConsentListItem> = [
      {
        title: 'マッサージ',
        key: 'massage',
        align: 'center',
        width: 80,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'massage', sortOrder as any),
        render: (_, record) => {
          const hasOrthomorphia = !!record.orthomorphia?.trim();
          const hasMassage = !!record.massage?.trim();
          return !hasOrthomorphia && hasMassage ? '○' : '';
        },
      },
      {
        title: '変形徒手',
        key: 'orthomorphia',
        align: 'center',
        width: 80,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'orthomorphia', sortOrder as any),
        render: (_, record) => {
          const hasOrthomorphia = !!record.orthomorphia?.trim();
          return hasOrthomorphia ? '○' : '';
        },
      },
      walkingConditionColumn,
    ];

    const actionColumn: ColumnsType<ConsentListItem>[number] = {
      title: '操作',
      key: 'actions',
      width: 170,
      align: 'center' as const,
      render: (_, record: any) => (
        <div className="flex justify-center gap-1">
          <AppTableActionButton
            action="edit"
            disabled={isBusy}
            title="編集"
            onClick={e => {
              e.stopPropagation();
              navigate(`/${tenant}/consents/${derived.patientId}/edit/${record.id}`);
            }}
          >
            編集
          </AppTableActionButton>
          <AppTableActionButton
            action="delete"
            disabled={isBusy}
            icon={<i className="fa fa-trash-o" aria-hidden="true" />}
            title="削除"
            onClick={e => {
              e.stopPropagation();
              confirm({
                title: '削除確認',
                content: (
                  <div>
                    <p>{DELETE_CONFIRM_DATA_MESSAGE}</p>
                  </div>
                ),
                okButtonProps: { danger: true },
                onOk: async () => {
                  await handleDeleteConfirm(record);
                },
                okText: 'OK',
                cancelText: 'キャンセル',
              });
            }}
          >
            削除
          </AppTableActionButton>
        </div>
      ),
    };

    return isR2
      ? [baseColumns[0], ...r2Columns, ...baseColumns.slice(1), actionColumn]
      : [baseColumns[0], walkingConditionColumn, ...baseColumns.slice(1), actionColumn];
  }, [
    sortField,
    sortOrder,
    tenant,
    derived.patientId,
    navigate,
    confirm,
    handleDeleteConfirm,
    isR2,
  ]);

  if (isError && !listData) {
    return <NotFound />;
  }

  return (
    <div className="relative">
      {isFetching && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="flex items-center gap-3 text-gray-700">
            <AppSpin size="large" />
          </div>
        </div>
      )}

      <CollapsibleCard title="同意書一覧">
        <PatientPageHeader
          patient={patient}
          patientId={derived.patientId}
          tenant={tenant || ''}
          navigate={navigate}
          activeKey="consents"
          disabled={isBusy}
          actions={
            <div className="flex gap-8">
              <AppButton
                type="primary"
                disabled={isBusy}
                icon={<i className="fa fa-file-text-o" />}
                onClick={() => navigate(`/${tenant}/consents/${derived.patientId}/create`)}
              >
                新規同意書
              </AppButton>
              <AppButton
                type="primary"
                disabled={isBusy}
                icon={<i className="fa fa-files-o" />}
                onClick={() => navigate(`/${tenant}/consents/${derived.patientId}/continue`)}
              >
                {consents.length === 0 ? '特殊継続同意書' : '継続同意書'}
              </AppButton>
            </div>
          }
        />

        <div className="relative overflow-x-auto">
          <AppTable<ConsentListItem>
            rowKey={r => String(r.id)}
            columns={columns}
            dataSource={consents}
            pagination={false}
            bordered
            recordCount={pagination ? pagination.total : totalCount}
            onChange={handleSortChange}
            customLabelTotal="表示件数"
          />
        </div>

        {pagination && pagination.last_page > 1 && (
          <div className="flex justify-center mt-4">
            <AppPagination
              current={pagination.current_page}
              total={pagination.total}
              pageSize={pagination.per_page}
              onChange={handlePageChange}
              disabled={isBusy}
            />
          </div>
        )}
      </CollapsibleCard>
    </div>
  );
};
